
// components/useDebateRoom.js
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { storage, firestore, auth } from '../../firebase/firebase.js';
import { DebateEngine } from '../DebateEngine/DebateEngine.js';

export function useDebateRoom({ debateId, userId, userRole, debateTypeId, tickInterval = 1000, tickValue = 1 }) {
  const [engine, setEngine] = useState(null);
  const [state, setState] = useState(null);
  const [engineEvents, setEngineEvents] = useState([]);
  
  const isApplyingRemoteRef = useRef(false);
  const lastCommittedVersionRef = useRef(null);
  const tickIntervalRef = useRef(null);

  //console.log(`Initializing useDebateRoom with debateId=${debateId}, userId=${userId}, userRole=${userRole}, debateTypeId=${debateTypeId}`);

  /** -----------------------
   *  Firestore subscription
   * ----------------------- */
  useEffect(() => {
	if (!debateId) return;
  if (!debateId || !userId || !userRole || !debateTypeId) return;
  
	  const ref = doc(firestore, 'arenas', debateId);
    const unsub = onSnapshot(ref, snap => {
	  if (!snap.exists()) return;

	  const remote = snap.data();
		const teams = extractTeams(remote);
		const speakers = extractSpeakers(remote);

		if (!Object.keys(teams).length || !Object.keys(speakers).length) {
		  console.warn('Debate not ready: teams or speakers missing');
		  return;
		}
		
		if (!engine ||engine.debateId !== debateId) {
			const restored = remote.engineState && remote.engineState.debateTypeId == debateTypeId ? DebateEngine.fromSerialized(remote.engineState) : new DebateEngine({ debateId, debateTypeId });

			restored.setParticipants({ teams, speakers });
      restored.configureRounds(remote.custom?.rounds || 1);
			
			isApplyingRemoteRef.current = true;
			if (remote.engineState) {
				restored.hydrate(remote.engineState.state);
			}

			setEngine(restored);
			setState(restored.serialize().state);
			lastCommittedVersionRef.current = remote.version;
			isApplyingRemoteRef.current = false;
			
		} else {
			//console.log('Received remote update:', remote, engine.debateId, debateId, remote.engineState.debateId);
      if (remote.updatedBy === userId && engine.debateId === debateId) return;
			if(lastCommittedVersionRef.current !== null && remote.version <= lastCommittedVersionRef.current && engine.debateId === debateId) {
				return;
			}

			isApplyingRemoteRef.current = true;
			if (remote.engineState) {
				engine.hydrate(remote.engineState.state);
			}

      engine.setDebateType({ debateId, debateTypeId });
      engine.setParticipants({ teams, speakers });
			engine.configureRounds(remote.custom?.rounds || 1);
   

			setState(engine.serialize().state);
      setEngine(engine);
			lastCommittedVersionRef.current = remote.version;
			isApplyingRemoteRef.current = false;
		}
    });

    return () => unsub();
  }, [debateId, debateTypeId]);


  /** -----------------------
   *  Helpers to extract teams/speakers
   * ----------------------- */
  function extractTeams(doc) {
    const teams = {};
    doc.custom?.teams?.forEach((name, i) => {
      teams[name] = { id: name, name, maxSpeakers: doc.custom?.teamsnumber?.[i] ?? null };
    });
    return teams;
  }

  function extractSpeakers(doc) {
    const speakers = {};
    doc.roles?.debaters?.forEach(d => {
      speakers[d.uid] = { speakerId: d.uid, userId: d.uid, teamId: d.team, role: 'speaker', username: d.username, profilePicture: d.profilePicture };
    });
    return speakers;
  }

  /** -----------------------
   *  Capabilities
   * ----------------------- */
  const capabilities = useMemo(() => ({
    canStartDebate: engine?.can(userRole, 'startDebate'),
    canEndTurn: engine?.can(userRole, 'endTurn'),
    canPause: engine?.can(userRole, 'pauseDebate'),
    canResume: engine?.can(userRole, 'resumeDebate'),
    canSkipSpeaker: engine?.can(userRole, 'skipSpeaker'),
    canScore: engine?.can(userRole, 'updateScores'),
    canDeclareWinner: engine?.can(userRole, 'declareWinner'),
    canManageSpectators: engine?.can(userRole, 'manageSpectators'),
    canSubmitQuestion: engine?._canSpectator('questions'),
    canJoinSpeakerQueue: engine?._canSpectator('joinSpeakerQueue'),
    canAddComment: engine?._canSpectator('comments'),
    canCastVote: engine?._canSpectator('preVote') || engine?._canSpectator('postVote'),
    canPointsOfInformation: engine?._canSpectator('pointsOfInformation')
  }), [engine, engine?.debateId, userRole]);

  /** -----------------------
   *  Commit engine state
   * ----------------------- */
  const commitEngine = useCallback(async () => {
    if (!engine || isApplyingRemoteRef.current) return;

    const serialized = engine.serialize();
    if (serialized.state.version === lastCommittedVersionRef.current) return;

    lastCommittedVersionRef.current = serialized.state.version;

    const ref = doc(firestore, 'arenas', debateId);
    await updateDoc(ref, {
      engineState: serialized,
      version: serialized.state.version,
      updatedAt: Date.now(),
      updatedBy: userId
    });

    setState(serialized.state);
  }, [engine, debateId, userId]);

  const commitEngineForce = useCallback(async () => {
    if (!engine) return;
    const serialized = engine.serialize();
    lastCommittedVersionRef.current = 0;
    const ref = doc(firestore, 'arenas', debateId);
    await updateDoc(ref, {
      engineState: serialized,
      version: serialized.state.version,
      updatedAt: Date.now(),
      updatedBy: userId
    });
  }, [engine, debateId, userId]);

  /** ----------------------------
   * Engine-emitted events for automation
   * ---------------------------- */
  useEffect(() => {
    if (!engine) return;
    const listener = (event) => setEngineEvents(event);//(prev => [...prev, event]);
    engine.onEvent(listener);
    return () => engine.offEvent(listener);
  }, [engine]);
  
  /** -----------------------
   *  Auto-tick engine timers
   * ----------------------- */
  useEffect(() => {
    if (!engine || state?.status == 'ended' || tickInterval == 0) return;
	
    tickIntervalRef.current = setInterval(() => {
		engine.tick(tickValue);           // reduces remainingTimeForTurn / team
		setState(prev => ({ ...engine.serialize().state })); // trigger re-render
    }, tickInterval);

    return () => clearInterval(tickIntervalRef.current);
  }, [state?.status, engine, tickInterval]);

  /** -----------------------
   *  Active turn + timers
   * ----------------------- */
  const activeTurn = useMemo(() => {
    if (!engine) return null;
    const turn = engine.getActiveTurn();
    if (!turn) return null;

    let turnSpeakers = engine.getTurnSpeakers(turn);
    let queuedSpeakers = [];
    if (['fishbowl', 'townhall'].includes(debateTypeId)) {
      queuedSpeakers = engine.getActiveSpeakerQueue().map(s => s.speakerId);
    }

    return {
      ...turn,
      activeSpeakers: [...turnSpeakers, ...queuedSpeakers],
      remainingTimeForTurn: engine.remainingTimeForTurn(),
      remainingTimeForTeam: engine.remainingTimeForTeam(turn.team)
    };
  }, [state, engine, debateTypeId]);

  /** -----------------------
   *  Active speakers for UI
   * ----------------------- */
  const activeSpeakers = useMemo(() => {
    if (!engine) return [];
    const turn = engine.getActiveTurn();
    if (!turn) return [];
    const speakers = engine.getActiveSpeakers();
    return speakers.map(s => ({
      ...s,
      remainingTimeForSpeaker: engine.remainingTimeForSpeaker(s.speakerId),
      turnDescription: turn.description || ''
    }));
  }, [state, engine, activeTurn]);


	const lastEvent = useMemo(() => {
	  const events = state?.events;
	  return events?.length ? events[events.length - 1] : null;
	}, [state?.events?.length]);

  /** -----------------------
   *  Actions
   * ----------------------- */
  const actions = {
    startDebate: async () => capabilities.canStartDebate && (engine.startDebate(), await commitEngine()),
    endTurn: async () => capabilities.canEndTurn && (engine.endTurn(), await commitEngine()),
    skipSpeaker: async (speakerId) => capabilities.canSkipSpeaker && (engine.skipSpeaker(speakerId), await commitEngine()),
    setCurrentSpeaker: async (speakerId) => speakerHelpers.canRecord(speakerId) && (engine.setCurrentSpeaker(speakerId), await commitEngine()),
    updateScores: async (targetId, points) => capabilities.canScore && (engine.updateScores(targetId, points), await commitEngine()),
    declareWinner: async (winner) => capabilities.canDeclareWinner && (engine.declareWinner(winner), await commitEngine()),
    pauseDebate: async () => capabilities.canPause && (engine.pauseDebate(), await commitEngine()),
    resumeDebate: async () => capabilities.canResume && (engine.resumeDebate(), await commitEngine()),
    addMessage: async (speakerId, msg) => speakerHelpers.canRecord(speakerId) && (engine.addMessage(msg), await commitEngine()),
    resetDebate: async () => engine && (engine.reset(), await commitEngineForce()),
    debateSummary: () => engine && engine.debateSummary(),
    isengineReady: engine != null,
    configureRounds: (numRounds) => engine && (engine.configureRounds(numRounds), commitEngine())
  };

  /** -----------------------
   *  Speaker helpers
   * ----------------------- */
  const speakerHelpers = {
    canRecord: (speakerId) => engine?.canRecord(speakerId),
    remainingTimeForSpeaker: (speakerId) => engine?.remainingTimeForSpeaker?.(speakerId)
  };

  /** -----------------------
   *  Spectator actions
   * ----------------------- */
  const spectatorActions = {
	submitQuestion: async (msg) => capabilities.canSubmitQuestion && (engine.submitQuestion(msg), await commitEngine()),
	addComment: async (msg) => capabilities.canAddComment && (engine.addComment(msg), await commitEngine()),
	castVote: async (msg) => capabilities.canCastVote && (engine.castVote(msg), await commitEngine()),
	requestToSpeak: async () => capabilities.canjoinSpeakerQueue &&  (engine.requestToSpeak(userId), await commitEngine()),
	submitPOI: async (msg) => capabilities.canPointsOfInformation && (engine.submitPOI(msg), await commitEngine())
  };

  /** -----------------------
   *  Moderator actions
   * ----------------------- */
  const moderatorActions = {
	admitSpeaker: async (uid, teamId, duration) => capabilities.canManageSpectators && (engine.admitSpeakerFromQueue(uid, teamId, duration), await commitEngine())
  };

  /** -----------------------
   *  Return full state for UI
   * ----------------------- */
  return {
    state,
    currentRound: state?.currentRound,
    roundActive: state?.roundActive,
    activeTurn,
    activeSpeakers,
	  lastEvent,
    actions,
    capabilities,
    spectatorActions,
    speakerHelpers,
	  engineEvents,
    moderatorActions
  };
}